import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import './Management.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:1337';

const ProjectManagement = () => {
  const { token } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', date: '' });
  const [existingImages, setExistingImages] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [newFilePreviews, setNewFilePreviews] = useState([]);

  useEffect(() => {
    fetchProjects();
  }, [token]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/projects?populate=*`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch projects');
      const data = await response.json();
      setProjects(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const normalizeOrientation = (file) =>
    new Promise((resolve) => {
      createImageBitmap(file).then((bitmap) => {
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        bitmap.close();
        canvas.toBlob(
          (blob) => resolve(new File([blob], file.name, { type: blob.type })),
          file.type || 'image/jpeg',
          0.92
        );
      });
    });

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const normalized = await Promise.all(files.map(normalizeOrientation));
    const previews = normalized.map((f) => URL.createObjectURL(f));
    setNewFiles((prev) => [...prev, ...normalized]);
    setNewFilePreviews((prev) => [...prev, ...previews]);
    e.target.value = '';
  };

  const removeNewFile = (index) => {
    URL.revokeObjectURL(newFilePreviews[index]);
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
    setNewFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (id) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== id));
  };

  const resetForm = () => {
    newFilePreviews.forEach((url) => URL.revokeObjectURL(url));
    setFormData({ title: '', description: '', date: '' });
    setExistingImages([]);
    setNewFiles([]);
    setNewFilePreviews([]);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const method = editingId ? 'PUT' : 'POST';
      const endpoint = editingId
        ? `${API_URL}/api/projects/${editingId}`
        : `${API_URL}/api/projects`;

      const descriptionBlocks = formData.description
        ? [{ type: 'paragraph', children: [{ type: 'text', text: formData.description }] }]
        : [];

      // Save project metadata first
      const projectRes = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          data: { title: formData.title, description: descriptionBlocks, date: formData.date || null },
        }),
      });
      if (!projectRes.ok) {
        const err = await projectRes.json();
        throw new Error(err.message || 'Kan project niet opslaan');
      }
      const projectData = await projectRes.json();
      const projectId = projectData.data.id;

      // Upload new files if any
      let newImageIds = [];
      if (newFiles.length > 0) {
        const uploadData = new FormData();
        newFiles.forEach((f) => uploadData.append('files', f));
        const uploadRes = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: uploadData,
        });
        if (!uploadRes.ok) throw new Error('Afbeeldingen uploaden mislukt');
        const uploaded = await uploadRes.json();
        newImageIds = uploaded.map((f) => f.id);
      }

      const keptIds = existingImages.map((img) => img.id);
      const finalIds = [...keptIds, ...newImageIds];

      // Update project with final image list
      await fetch(`${API_URL}/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ data: { image: finalIds } }),
      });

      await fetchProjects();
      resetForm();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getDescriptionText = (desc) => {
    if (!desc) return '';
    if (typeof desc === 'string') return desc;
    if (Array.isArray(desc)) {
      return desc
        .map((block) =>
          block.children ? block.children.map((c) => c.text || '').join(' ') : ''
        )
        .join(' ');
    }
    return '';
  };

  const handleDelete = async (id) => {
    if (!confirm('Weet je zeker dat je dit project wilt verwijderen?')) return;
    try {
      const response = await fetch(`${API_URL}/api/projects/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to delete project');
      await fetchProjects();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (project) => {
    const attrs = project.attributes || project;
    const images = attrs.image?.data || attrs.image || [];
    const mapped = Array.isArray(images)
      ? images.map((img) => ({
          id: img.id,
          url: `${API_URL}${img.attributes?.url || img.url}`,
          name: img.attributes?.name || img.name || '',
        }))
      : [];

    setFormData({
      title: attrs.title || '',
      description: getDescriptionText(attrs.description),
      date: attrs.date || '',
    });
    setExistingImages(mapped);
    setNewFiles([]);
    setNewFilePreviews([]);
    setEditingId(project.id);
    setShowForm(true);
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Projectbeheer</h1>
        <button className="btn-primary" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? 'Annuleren' : '+ Project Toevoegen'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Projecttitel</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Beschrijving</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="4"
            />
          </div>
          <div className="form-group">
            <label>Datum</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            />
          </div>

          <div className="form-group">
            <label>Afbeeldingen</label>

            {existingImages.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <p style={{ fontSize: '13px', color: '#aaa', marginBottom: '8px' }}>
                  Huidige afbeeldingen:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {existingImages.map((img) => (
                    <div key={img.id} style={{ position: 'relative' }}>
                      <img
                        src={img.url}
                        alt={img.name}
                        style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px', display: 'block' }}
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(img.id)}
                        style={{
                          position: 'absolute', top: '-6px', right: '-6px',
                          width: '20px', height: '20px', borderRadius: '50%',
                          background: '#e53e3e', color: '#fff', border: 'none',
                          cursor: 'pointer', fontSize: '12px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {newFilePreviews.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                <p style={{ fontSize: '13px', color: '#aaa', marginBottom: '8px' }}>
                  Nieuwe afbeeldingen:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {newFilePreviews.map((src, i) => (
                    <div key={i} style={{ position: 'relative' }}>
                      <img
                        src={src}
                        alt=""
                        style={{ width: '80px', height: '60px', objectFit: 'cover', borderRadius: '6px', display: 'block' }}
                      />
                      <button
                        type="button"
                        onClick={() => removeNewFile(i)}
                        style={{
                          position: 'absolute', top: '-6px', right: '-6px',
                          width: '20px', height: '20px', borderRadius: '50%',
                          background: '#e53e3e', color: '#fff', border: 'none',
                          cursor: 'pointer', fontSize: '12px', lineHeight: 1,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <input type="file" multiple accept="image/*" onChange={handleFileChange} />
          </div>

          <button type="submit" className="btn-submit" disabled={saving}>
            {saving ? 'Bezig met opslaan...' : editingId ? 'Project Bijwerken' : 'Project Aanmaken'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="loading">Projecten worden geladen...</p>
      ) : projects.length === 0 ? (
        <p className="empty">Geen projecten gevonden</p>
      ) : (
        <div className="items-table">
          <table>
            <thead>
              <tr>
                <th>Titel</th>
                <th>Datum</th>
                <th>Foto's</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => {
                const attrs = project.attributes || project;
                const images = attrs.image?.data || attrs.image || [];
                const count = Array.isArray(images) ? images.length : 0;
                const thumb = count > 0
                  ? `${API_URL}${images[0].attributes?.url || images[0].url}`
                  : null;
                return (
                  <tr key={project.id}>
                    <td>{attrs.title || ''}</td>
                    <td>{attrs.date ? new Date(attrs.date).toLocaleDateString('nl-NL') : '-'}</td>
                    <td>
                      {thumb ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <img
                            src={thumb}
                            alt=""
                            style={{ width: '48px', height: '36px', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          {count > 1 && (
                            <span style={{ fontSize: '12px', color: '#aaa' }}>+{count - 1}</span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#aaa', fontSize: '12px' }}>Geen</span>
                      )}
                    </td>
                    <td className="actions">
                      <button className="btn-edit" onClick={() => handleEdit(project)}>
                        Bewerken
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(project.id)}>
                        Verwijderen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;
